import { watch } from 'vue';

import { invalidateNotifications } from '@/domains/notifications/queries';
import { getSubBus } from '@/platform/host-sdk/host-bridge';
import { refreshAccountInfo } from '@/platform/identity/queries';
import { useUserStore } from '@/platform/identity/store';
import { reportManualError } from '@/platform/telemetry/frontend-alert';
import { queryClient } from '@/shared/lib/query-client';
import { parseWsFrame, wsData } from '@/shared/lib/ws/ws';
import { HOST_EVENTS, emitMessage } from '@celerdata/host-bridge-contract';
import { useDebounceFn } from '@vueuse/core';

import type { FormattedUnreadList } from '@/domains/notifications/queries';

const FORCED_LOGOUT_CODE = 50001;
const UNREAD_KEY = ['notification', 'unread-list'] as const;

// Shared with queries.ts mutations — invalidates the v3 unread cache AND
// emits the host bridge invalidate signal so the legacy /notification view
// refetches its own list. Debounced 1s to coalesce rapid WS bursts.
const refresh = useDebounceFn(() => {
  invalidateNotifications(queryClient);
}, 1000);

function currentUnreadKey(): readonly unknown[] {
  return [...UNREAD_KEY, useUserStore().info?.accountID ?? null];
}

function removeBannerLocally(notificationId: string): boolean {
  const key = currentUnreadKey();
  const prev = queryClient.getQueryData<FormattedUnreadList>(key);
  const banners = prev?.barNotifyList?.notify_msgs;
  if (!banners?.length) return false;
  const next = banners.filter((b) => b.biz_id !== notificationId);
  if (next.length === banners.length) return false;
  queryClient.setQueryData<FormattedUnreadList>(key, {
    ...prev,
    barNotifyList: { ...prev!.barNotifyList, notify_msgs: next },
  });
  return true;
}

// #1644: this used to be its own confirm whose Cancel left the user on the
// page. That promise was hollow — the session is already gone, so the next
// request came back 50001 and handleAuthError ejected them with different
// wording anyway — and it was the one path that ended a sign-out WITHOUT
// navigating, leaving the legacy sub's request gate latched for the session.
// Delegating gives one dialog, one wording, always a redirect; handleAuthError
// owns the shared lock, so the concurrency guard is preserved. Lazily imported
// to keep the axios graph (which reaches element-plus) out of startup.
async function promptReLogin(): Promise<void> {
  const { handleAuthError } = await import('@/shared/lib/http/axios');
  await handleAuthError();
}

type WsMessage = {
  type?: string;
  code?: number;
  payload?: {
    type?: string;
    notificationId?: string;
    accountId?: string;
  };
};

// No payload is reported: a frame can carry user data.
function reportWsError(type: 'ws_listener_error', ex: unknown): void {
  reportManualError(type, (ex instanceof Error ? ex.message : String(ex)) || 'websocket error', {
    stack: ex instanceof Error ? ex.stack : 'No stack trace',
  });
}

watch(wsData, (raw) => {
  const msg = parseWsFrame(raw);
  if (!msg) return;
  const m = msg as WsMessage;
  try {
    if (m.code === FORCED_LOGOUT_CODE) {
      void promptReLogin();
    } else if (m.type === 'GlobalEventTypeNotificationReminder') {
      void refresh();
    } else if (m.type === 'GlobalEventTypeNotificationBroadcast') {
      if (m.payload?.type === 'publish') {
        void refresh();
      } else if (m.payload?.type === 'recall' && m.payload.notificationId) {
        if (removeBannerLocally(m.payload.notificationId)) void refresh();
      }
    } else if (m.type === 'GlobalEventTypePermissionChanged') {
      // Refresh perms; broadcaster's state-registry watch on user.perms /
      // user.dataPerms re-fires host:auth-changed, sub mirror updates, and
      // legacy router's permsWatcher (Task 3.8 follow-up) rebuilds matcher.
      void useUserStore().fetchPerms();
    } else if (m.type === 'GlobalEventTypeAccountStatusChanged' && m.payload?.accountId) {
      // Force a fresh fetch (not just invalidate) AND signal sub. The
      // state=-2 unsubscribe sign-out lives in the queryFn, so it must run
      // even on chrome-less routes where no component observes the query.
      refreshAccountInfo(queryClient, m.payload.accountId);
    }
  } catch (ex) {
    reportWsError('ws_listener_error', ex);
  }
  // Relay the parsed frame to sub, isolated from the handlers above. Host owns
  // the WS post-carving, but legacy views still register `ws.addMsgListener`
  // (cluster state, schedule policy, support permission). A throwing v3 handler
  // must not starve this relay — legacy guards each listener the same way.
  try {
    emitMessage(getSubBus(), HOST_EVENTS.WS_PUSH, m as Record<string, unknown>);
  } catch (ex) {
    reportWsError('ws_listener_error', ex);
  }
});
